import { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import Checkbox from '@mui/material/Checkbox';
import TableCell from '@mui/material/TableCell';
import { pluck } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import { PKUrl } from 'components';
import { Airplane, Car, Combinated, Investment, Liability, Live2 } from 'components/atoms/CustomSVGIcon';
import { House, JourneyMilestones } from 'components/atoms/SvgrIcons';
import { changeCheckBoxValue, updateCheckAllCheckbox } from 'features/views/roboAdvisory/actions';
import { raAnalysisTypes } from 'features/views/roboAdvisory/constants';
import { getIsRaAnalysisCheckedById } from 'features/views/roboAdvisory/selectors';
import { getHasSubordinates } from 'features/views/user/selectors';
import { equalOrThreeDashes, fromIsoDate } from 'utils';
import { generateFullNameOrBusinessName } from 'utils/string';

import { generateProductGroups, generateStateValue, generateTypeValue } from '../utils';

const RATableRow = ({ record, raTypesCodelist, raStatesCodelist, raProductGroupsCodelist }) => {
	const dispatch = useDispatch();

	const { createdOn = '', type = '', state = '', partner = {}, products = [], policies = [], id } = record;
	const { juridicalPerson, naturalPerson, address, unifiedPartner: ccdId } = partner;

	const isRaAnalysisChecked = useSelector((state) => getIsRaAnalysisCheckedById(state, id));
	const hasSubordinates = useSelector(getHasSubordinates);

	const isNotOnboardingType = type !== raAnalysisTypes.ONBOARDING;

	const handleChangeCheckBox = useCallback(
		(id) => () => {
			dispatch(changeCheckBoxValue(id));
			dispatch(updateCheckAllCheckbox());
		},
		[dispatch],
	);

	const generateRAIcon = (type) => {
		switch (type) {
			case raAnalysisTypes.ONBOARDING:
				return <JourneyMilestones />;
			case raAnalysisTypes.SMART_INVEST:
				return <Investment />;
			case raAnalysisTypes.SMART_LIFE:
				return <Live2 />;
			case raAnalysisTypes.SMART_LIFE_INV:
				return <Combinated />;
			case raAnalysisTypes.SMART_PROPERTY:
				return <House />;
			case raAnalysisTypes.SMART_RESPONS:
				return <Liability />;
			case raAnalysisTypes.SMART_TRAVEL:
				return <Airplane />;
			case raAnalysisTypes.SMART_AUTO:
				return <Car />;
		}
	};

	const generateContextualTypes = (policies) => {
		const contextualInfos = pluck('contextualInformation')(policies);

		if (isNilOrEmpty(contextualInfos)) {
			return equalOrThreeDashes();
		} else {
			return contextualInfos.map((info, i) => (
				<Fragment key={i}>
					{info}
					<br />
				</Fragment>
			));
		}
	};

	return (
		<>
			<TableCell sx={{ color: 'primary.main', fontSize: '25px' }}>{generateRAIcon(type)}</TableCell>
			<TableCell>
				<div>{equalOrThreeDashes(generateTypeValue(type, raTypesCodelist))}</div>
				<div>{equalOrThreeDashes(generateStateValue(state, raStatesCodelist))}</div>
			</TableCell>
			<TableCell>
				<PKUrl.PKPartner partnerId={ccdId.id} muiLink>
					{generateFullNameOrBusinessName(
						juridicalPerson?.name,
						naturalPerson?.surname,
						naturalPerson?.firstName,
					)}
				</PKUrl.PKPartner>
			</TableCell>
			<TableCell>
				<div>{equalOrThreeDashes(naturalPerson?.birthNo)}</div>
				<div>{equalOrThreeDashes(juridicalPerson?.identificationNumber)}</div>
			</TableCell>
			<TableCell>{generateContextualTypes(policies)}</TableCell>
			<TableCell>{generateProductGroups(products, raProductGroupsCodelist)}</TableCell>
			<TableCell>
				<div>{equalOrThreeDashes(address?.municipality)}</div>
				<div>{equalOrThreeDashes(address?.zipCode)}</div>
			</TableCell>
			<TableCell>
				<div>{equalOrThreeDashes(fromIsoDate(createdOn))}</div>
			</TableCell>
			{hasSubordinates && (
				<TableCell align="right">
					{isNotOnboardingType && (
						<Checkbox checked={isRaAnalysisChecked} onChange={handleChangeCheckBox(id)} />
					)}
				</TableCell>
			)}
		</>
	);
};

RATableRow.propTypes = {
	raProductGroupsCodelist: PropTypes.array,
	raStatesCodelist: PropTypes.array,
	raTypesCodelist: PropTypes.array,
	record: PropTypes.object,
};

export default RATableRow;
